import { Suspense } from "react";
import { Await, Link } from "react-router";
import { ClientOnly } from "remix-utils/client-only";

import { NavigationMenuLink } from "~/components/ui/navigation-menu";
import { useURL } from "~/contexts";
import { toProductUrl } from "~/lib/utils/url";
import { useRootLayoutData } from "~/root";
import { SearchSuggestionsResource } from "~/routes/resources/search-suggestions";
import { WishlistCTA } from "~/routes/resources/wishlist";
import { useFormatPrice } from "~/shop/utils/price";

import type {
  BrandSuggestions,
  CategorySuggestions,
  CustomSuggestions,
  ProductSuggestions,
  SuggestedCategory,
  SuggestedPhrase,
  SuggestedProduct,
} from "../types";
import { Spinner } from "../ui/icons/spinner";
import Image from "../ui/image";
import { P } from "../ui/text";

export const MeganavSearchBarSuggestions = ({
  q,
  isSearchOverlay = false,
}: {
  q: string;
  isSearchOverlay?: boolean;
}) => {
  const url = useURL();
  const { DEFAULT_SEARCH_TERM } = useRootLayoutData();
  return (
    <ClientOnly>
      {() => (
        <SearchSuggestionsResource q={q}>
          {(data, state) => {
            return (
              <div className="z-20 w-full rounded-lg bg-white">
                {state === "loading" && (
                  <div className="flex h-screen w-full items-center justify-center">
                    <Spinner className="mx-auto my-auto h-8 w-8 opacity-50" />
                  </div>
                )}
                <Suspense>
                  <Await resolve={data?.suggestions}>
                    {suggestions => {
                      if (!suggestions) return null;
                      const {
                        productSuggestions,
                        categorySuggestions,
                        brandSuggestions,
                      } = suggestions;
                      const combinedSuggestions =
                        productSuggestions?.suggestedPhrases?.filter(
                          (phrase: { exactMatch: any }) => !phrase.exactMatch,
                        );
                      const alternativeSuggestions: { phrase: string }[] =
                        brandSuggestions?.suggestedPhrases?.filter(
                          (phrase: unknown) =>
                            typeof phrase === "object" &&
                            phrase &&
                            "exactMatch" in phrase &&
                            !phrase.exactMatch &&
                            "phrase" in phrase,
                        );
                      const hasSuggestions =
                        productSuggestions?.products?.length ||
                        categorySuggestions?.categories?.length ||
                        alternativeSuggestions?.length ||
                        combinedSuggestions?.length;

                      return (
                        <>
                          {!hasSuggestions && (
                            <P className="text-center">
                              Sorry, no results for{" "}
                              <span className="font-bold">‘{q}’ </span>
                              Try a different search or explore our latest
                              collection.
                            </P>
                          )}
                          <ul className="grid w-auto animate-reveal grid-cols-[1fr] gap-2 bg-white">
                            {hasSuggestions &&
                              suggestions.searchPhrase !==
                                DEFAULT_SEARCH_TERM && (
                                <>
                                  <li className="row-span-3 pb-2">
                                    <div className="flex h-[100%] flex-col items-stretch">
                                      <P className="text-left text-base font-semibold">
                                        Suggestions
                                      </P>
                                      {combinedSuggestions && (
                                        <SearchSuggestions
                                          title={""}
                                          searchTerm={suggestions.searchPhrase}
                                          suggestions={
                                            combinedSuggestions as SuggestedPhrase[]
                                          }
                                        />
                                      )}

                                      {categorySuggestions?.categories
                                        ?.length && (
                                        <SearchSuggestions
                                          title={""}
                                          searchTerm={suggestions.searchPhrase}
                                          suggestions={
                                            categorySuggestions.categories as SuggestedCategory[]
                                          }
                                        />
                                      )}

                                      {alternativeSuggestions?.length && (
                                        <SearchSuggestions
                                          title={""}
                                          searchTerm={suggestions.searchPhrase}
                                          suggestions={
                                            alternativeSuggestions as BrandSuggestions[]
                                          }
                                        />
                                      )}
                                    </div>
                                  </li>
                                  <p className="border-b-2"></p>
                                </>
                              )}
                            {hasSuggestions &&
                              (suggestions.searchPhrase ===
                              DEFAULT_SEARCH_TERM ? (
                                <P className="text-base font-semibold">
                                  Popular Search Items
                                </P>
                              ) : (
                                <P className="text-base font-semibold">Items</P>
                              ))}
                            <li className="mt-[22px] grid grid-cols-2 gap-2 md:grid-cols-3">
                              <ProductImageSuggestions
                                products={
                                  productSuggestions?.products as SuggestedProduct[]
                                }
                                isSearchOverlay={isSearchOverlay}
                              />
                            </li>
                          </ul>
                          <div className="sticky bottom-0 pb-4">
                            {combinedSuggestions && (
                              <Link
                                className="mt-2 inline-block w-full rounded-full border border-viewAllBtn bg-viewAllBtn p-2.5 text-center text-sm font-semibold text-white shadow-sm hover:border-neutral-8 hover:no-underline hover:shadow-none"
                                id="view-all-results"
                                to={url(
                                  `/search?q=${encodeURIComponent(
                                    suggestions.searchPhrase,
                                  )}`,
                                )}
                              >
                                View All
                              </Link>
                            )}
                          </div>
                        </>
                      );
                    }}
                  </Await>
                </Suspense>
              </div>
            );
          }}
        </SearchSuggestionsResource>
      )}
    </ClientOnly>
  );
};

function highlightMatch(suggestion: string, searchTerm: string) {
  if (!searchTerm) return suggestion;

  const searchWords = searchTerm
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean);

  if (searchWords.length === 0) {
    return suggestion;
  }

  const regex = new RegExp(`(${searchWords.join("|")})`, "gi");
  const parts = suggestion.split(regex);

  return (
    <>
      {parts.map((part, index) => {
        if (searchWords.some(word => word === part.toLowerCase())) {
          return (
            <span key={index} className="font-bold text-black">
              {part}
            </span>
          );
        }
        return <span key={index}>{part}</span>;
      })}
    </>
  );
}

export const SearchSuggestions = ({
  title,
  searchTerm,
  suggestions,
}: {
  title: string;
  searchTerm: string;
  suggestions:
    | BrandSuggestions[]
    | CategorySuggestions[]
    | ProductSuggestions[]
    | CustomSuggestions[]
    | SuggestedPhrase[];
}) => {
  const url = useURL();
  return (
    <>
      {title && (
        <P
          variant="muted"
          className="pt-4 text-base font-medium text-neutral-12 first:pt-0"
        >
          {title}
        </P>
      )}
      {suggestions?.map(suggestion => (
        <NavigationMenuLink
          key={`suggestion-${suggestion.name || suggestion.phrase}`}
          to={url(
            `/search?q=${encodeURIComponent(
              suggestion.id || suggestion.phrase,
            )}`,
          )}
          className={`block pt-2 text-sm font-light text-accent-12`}
          id="search-suggestion"
        >
          {highlightMatch(suggestion.name || suggestion.phrase, searchTerm)}
        </NavigationMenuLink>
      ))}
    </>
  );
};

export const ProductImageSuggestions = ({
  products,
  isSearchOverlay,
}: {
  products: ProductSuggestions[] | SuggestedProduct[];
  isSearchOverlay: boolean;
}) => {
  const url = useURL();
  const formatPrice = useFormatPrice();
  return products?.map(product => (
    <NavigationMenuLink
      key={`product-suggestion-${product.productId}`}
      to={
        url(
          toProductUrl({
            id: product?.productId,
          }),
        ) + ".html"
      }
      className="block text-sm font-light"
      id="search-suggestion"
    >
      <div className={`flex flex-col ${!isSearchOverlay && "sm:w-1/2"}`}>
        <Image
          src={product?.c_image}
          alt={product?.productName}
          className="rounded-xl object-cover"
          width="100%"
          height={181}
        />
        <button
          onClick={e => {
            e.preventDefault();
            e.stopPropagation;
          }}
        >
          <WishlistCTA
            productId={product.productId}
            className="absolute right-3 top-[13px]"
            iconHeight={10}
            iconWidth={10}
          />
        </button>
        <div className="px-2 py-2 text-neutral-12">
          <P className="text-sm font-normal">{product?.productName}</P>
          <P className="text-xs">
            {product?.c_pricing?.standardprice ? (
              <>
                <span className="line-through">
                  {formatPrice(product?.c_pricing?.standardprice)}
                </span>
                <span className="pl-1 text-salePrice">
                  {formatPrice(product?.c_pricing?.salesprice)}
                </span>
              </>
            ) : (
              formatPrice(product?.price)
            )}
          </P>
          <P className="text-sm font-normal">{product?.categoryName}</P>
        </div>
      </div>
    </NavigationMenuLink>
  ));
};
